
import { computed, defineComponent, PropType } from 'vue';
import { distinct } from '../../../helpers/array';
import { getFeatureColourFromBackground, getForegroundColourFromBackground } from '../../../helpers/colour';
import TpxButton from '../../atoms/button/button.vue';
import TpxFilterMenu from '../../molecules/filter/filter-menu.vue';
import TpxHeading from '../../atoms/type/heading.vue';
import TpxIcon from '../../atoms/icon/icon.vue';
import TpxLink from '../../atoms/link/link.vue';
import TpxListing from '../../molecules/listing/listing.vue';
import TpxParagraph from '../../atoms/type/paragraph.vue';
import './list.scss';

/**
 * List
 *
 * Items can either be passed in an object using <code>:item="..."</code>
 * or the list organism can make a request to an endpoint and retrieve list items from there using <code>:endpoint="..."</code>.
 * 
 * @example
 * <code>import TpxList from '@manifesto-tech/tpximpact-design-system/components/organisms/list/list.vue'</code>
 */
export default defineComponent({
  name: 'TpxList',
  extends: TpxListing,

  components: {
    TpxButton,
    TpxFilterMenu,
    TpxHeading,
    TpxIcon,
    TpxLink,
    TpxListing,
    TpxParagraph,
  },

  props: {
    /**
     * Specify the endpoint in which to retrieve list items from
     * The endpoint must return <code>{ items: IFilterableListItem[] }</code>
     */
    endpoint: {
      type: String,
    },

    /**
     * Optional heading for list
     */
    heading: {
      type: String,
    },

    /**
     * Optional link to display alongside the heading
     * Such as a 'see more' link
     */
    link: {
      type: Object as PropType<typeof TpxLink>,
    },

    /**
     * Number of items to display per page
     */
    paginationSize: {
      default: 10,
      type: Number,
    },
  },

  methods: {
    /**
     * Apply active filters to the data set
     */
    applyFilters() {
      this.filteredItems = this.allItems;

      const activeFilters = this.filterGroups
        .reduce((res, filterGroup) => res.concat(filterGroup.checkboxes.filter(checkbox => checkbox.isChecked)), []);

      const activeItems = [];
      activeFilters.forEach(activeFilter => {
        const selection = this.filteredItems
          .filter(item => item.filters?.some(filter =>
            filter.filter == activeFilter.label
            && (!filter.group || filter.group == activeFilter.name)
          ));

        selection.forEach(item => activeItems.push(item));
      });

      if (activeItems.length > 0) {
        this.filteredItems = [...new Set(activeItems)];
      }

      this.paginate(1);

      // Visual clue things have changed
      this.$refs.root.classList.remove('o-list--invoke-change');
      setTimeout(() => this.$refs.root.classList.add('o-list--invoke-change'), 1);

      // Scroll element into view
      if (this.$refs.root.getBoundingClientRect().top < 0) {
        this.$refs.root.scrollIntoView();
      }
    },

    /**
     * Load data from the endpoint
     * @returns {Promise<[]>} List of items to be displayed within the list
     */
    async loadData(): Promise<[]> {
      this.isLoading = true;
      return await fetch(this.endpoint)
        .then(response => {
          return response.json();
        })
        .then(data => {
          return data.items;
        })
        .catch(error => {
          this.hasErrored = true;
          console.error('Error retrieving list data', error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    /**
     * Increases visible set by a pages worth
     */
    loadMore() {
      this.paginate(this.activePage + 1);
    },

    /**
     * Paginate the results
     */
    paginate(activePage: number) {
      this.activePage = activePage;
      this.activeItems = this.filteredItems?.slice(0, this.activePage * this.paginationSize);
      this.hasMore = this.filteredItems?.length > this.activeItems?.length;
    },
  },

  async created() {
    this.filteredItems = this.allItems = this.endpoint ? await this.loadData() : this.items;


    // Filters
    const filters: { filter: string, group?: string }[] =
      this.allItems
        ?.filter(item => item.filters?.length > 0)
        .reduce((res, item) => res.concat(item.filters), []);

    const filterGroups: { group?: string, filters: { label: string, key: string }[] }[] =
      filters
        ?.map(filter => filter.group)
        .filter(distinct)
        .sort()
        .map(group => {
          return {
            group,
            filters: filters
              .filter(filter => filter.group == group)
              .map(filter => filter.filter)
              .filter(distinct)
              .sort()
              .map(filter => {
                return {
                  label: filter,
                  key: group ? `${group?.replace(' ', '-')}__${filter.replace(' ', '-')}` : filter.replace(' ', '-'),
                }
              })
              .filter(filter => filter.label !== 'Experience'),
          }
        });

    this.filterGroups = filterGroups?.map(filterGroup => {
      return {
        label: filterGroup.group ? `${filterGroup.group}` : 'Filter',
        checkboxes: filterGroup.filters.map(filter => {
          return {
            id: filter.key,
            label: filter.label,
            name: filterGroup.group ?? 'filter',
            value: filter.label,
          }
        }),
      }
    });

    this.hasFilters = this.filterGroups?.length > 0;
    this.isLoading = false;


    /** 
     * If url has search parameters, apply those filters BUT if url has NO search parameters, do nothing
    */
    const searchParams = new URLSearchParams(window.location.search);
    const activeSectorFilters = searchParams.getAll('Sector');
    const activeServiceFilters = searchParams.getAll('Service');
    const activeTopicFilters = searchParams.getAll('Topic');

    const allActiveFilters = [...activeSectorFilters, ...activeServiceFilters, ...activeTopicFilters];

    if (allActiveFilters.length === 0) {
      //do nothing
    }
    else {
      this.filteredItems = this.allItems.filter((item) => {
        let itemSectorTags = [];
        let itemServiceTags = [];
        let itemTopicTags = [];

        item.filters.forEach((filterObj) => {
          if (filterObj.group === 'Sector') { itemSectorTags.push(filterObj.filter) }
          else if (filterObj.group === 'Service') { itemServiceTags.push(filterObj.filter) }
          else { itemTopicTags.push(filterObj.filter) }
        })

        let itemSectorTagsMatchesAtLeastOneActiveSectorFilter = false;
        activeSectorFilters.forEach((activeSectorFilter) => {
          if (itemSectorTags.includes(activeSectorFilter)) { itemSectorTagsMatchesAtLeastOneActiveSectorFilter = true; }
        })

        let itemServiceTagsMatchesAtLeastOneActiveServiceFilter = false;
        activeServiceFilters.forEach((activeServiceFilter) => {
          if (itemServiceTags.includes(activeServiceFilter)) { itemServiceTagsMatchesAtLeastOneActiveServiceFilter = true; }
        })

        let itemTopicTagsMatchesAtLeastOneActiveTopicFilter = false;
        activeTopicFilters.forEach((activeTopicFilter) => {
          if (itemTopicTags.includes(activeTopicFilter)) { itemTopicTagsMatchesAtLeastOneActiveTopicFilter = true; }
        })

        if (itemSectorTagsMatchesAtLeastOneActiveSectorFilter || itemServiceTagsMatchesAtLeastOneActiveServiceFilter || itemTopicTagsMatchesAtLeastOneActiveTopicFilter) {
          return true;
        }
        else {
          return false;
        }

      })
    }

    // Paginate
    this.paginate(1);
  },

  data() {
    return {
      activeItems: [],
      activePage: 1,
      allItems: [],
      colourClasses: computed(() => ({
        [`bg--${this.backgroundColour}`]: !!this.backgroundColour,
        [`text--${getForegroundColourFromBackground(this.backgroundColour)}`]: !!this.backgroundColour,
      })),
      featureClasses: computed(() => ({
        [`text--${getFeatureColourFromBackground(this.backgroundColour)}`]: !!this.backgroundColour,
      })),
      featureColour: getFeatureColourFromBackground(this.backgroundColour),
      filterGroups: [],
      filteredItems: [],
      hasErrored: false,
      hasFilters: false,
      hasMore: false,
      isLoading: false,
    }
  },
});
